/* custom-mamacool-attribution.js
   Two jobs, one small file, loaded on every page from layout/theme.liquid.

   1. Diagnostics. Each pageview posts a beacon to api.mamacool.com/api/track
      saying whether cookies and localStorage worked, whether our first-party
      visitor id survived from the last page, whether Shopify's own session
      cookie changed, what the consent state is, and whether Shopify's
      analytics had loaded by the time the shopper left. Rows land in
      Supabase track_events. Built after 2026-09-07, when a third of
      sessions started on product pages reached from the collection grid.

   2. Attribution onto the order. At the landing pageview (while the
      referrer, UTMs and click ids are still in hand) we write _mc_* cart
      attributes with POST /cart/update.js, and again after add-to-cart. The
      cart rides the server-side cart token, so these reach the order as
      note_attributes even when the browser wipes cookies and storage
      between pages. Underscore keys are hidden from the customer.

   Rules: never block, never show UI, never throw. Nothing here touches
   /cart/add.js or checkout. Consent: when Shopify's Customer Privacy API
   says analytics is not allowed, we write no ids and no cart attributes,
   and the beacon carries only anonymous diagnostics. README-attribution.md. */
(function () {
  var API = 'https://api.mamacool.com/api/track';
  var noop = function () {};
  var script = document.currentScript || document.querySelector('script[src*="custom-mamacool-attribution"]');
  var ds = (script && script.dataset) || {};
  var startedAt = Date.now();
  var t0 = (window.performance && performance.now()) || 0;

  /* ---------- helpers ---------- */
  function cookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }
  function setCookie(name, value, days) {
    try {
      document.cookie = name + '=' + encodeURIComponent(value) + (days ? '; max-age=' + (days * 86400) : '') + '; path=/; SameSite=Lax; Secure';
    } catch (e) { /* ignore */ }
  }
  function delCookie(name) { try { document.cookie = name + '=; max-age=0; path=/; SameSite=Lax; Secure'; } catch (e) { /* ignore */ } }
  function ls(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function setLs(k, v) { try { localStorage.setItem(k, v); return true; } catch (e) { return false; } }
  function json(s) { try { return JSON.parse(s); } catch (e) { return null; } }
  function rid() {
    var s = '';
    try {
      var a = new Uint8Array(8); crypto.getRandomValues(a);
      for (var i = 0; i < a.length; i++) s += ('0' + a[i].toString(16)).slice(-2);
    } catch (e) { s = (Math.random().toString(16) + Math.random().toString(16)).replace(/0\./g, '').slice(0, 16); }
    return s;
  }
  function send(payload) {
    try {
      fetch(API, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).catch(noop);
    } catch (e) { /* ignore */ }
  }

  /* ---------- who and where ---------- */
  var ua = navigator.userAgent || '';
  var isBot = /bot|crawl|spider|slurp|headless|lighthouse|facebookexternalhit|preview/i.test(ua) || navigator.webdriver === true;
  var uaClass = /musical_ly|Bytedance|TikTok/i.test(ua) ? 'tiktok'
    : /Instagram/i.test(ua) ? 'instagram'
    : /FBAN|FBAV|FB_IAB/i.test(ua) ? 'facebook'
    : /; wv\)|Line\/|Snapchat|Pinterest\/|GSA\//i.test(ua) ? 'other_inapp'
    : 'none';

  var path = location.pathname + location.search;
  var params = new URLSearchParams(location.search);
  var utm = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ttclid', 'fbclid', 'gclid', 'gad_source', 's', 'mc_from'].forEach(function (k) {
    var v = params.get(k); if (v) utm[k] = v.slice(0, 200);
  });
  var hasUtm = !!(utm.utm_source || utm.ttclid || utm.fbclid || utm.gclid);

  var referrer = (document.referrer || '').slice(0, 300);
  var refHost = '';
  try { refHost = referrer ? new URL(referrer).hostname.replace(/^www\./, '') : ''; } catch (e) { /* ignore */ }
  var ownHost = location.hostname.replace(/^www\./, '');
  var refInternal = !!refHost && (refHost === ownHost || /\.myshopify\.com$/.test(refHost) || /(^|\.)shop\.app$/.test(refHost));
  var landing = !refInternal;                    // entered the site on this page
  var strongLanding = landing && (hasUtm || !!refHost); // a landing with a real signal

  function refSource(host) {
    if (!host) return '';
    if (/instagram\.com$/.test(host)) return 'instagram';
    if (/tiktok\.com$/.test(host)) return 'tiktok';
    if (/facebook\.com$|fb\.com$|messenger\.com$/.test(host)) return 'facebook';
    if (/google\./.test(host)) return 'google';
    if (/bing\.com$/.test(host)) return 'bing';
    if (/pinterest\./.test(host)) return 'pinterest';
    if (/youtube\.com$|youtu\.be$/.test(host)) return 'youtube';
    return host.slice(0, 60);
  }

  /* ---------- storage checks ---------- */
  var cookieOk = false, storageOk = false;
  try { setCookie('_mc_t', '1'); cookieOk = cookie('_mc_t') === '1'; delCookie('_mc_t'); } catch (e) { /* ignore */ }
  try { localStorage.setItem('mc_t', '1'); storageOk = localStorage.getItem('mc_t') === '1'; localStorage.removeItem('mc_t'); } catch (e) { /* ignore */ }

  /* ---------- consent ---------- */
  // Shopify writes _tracking_consent when a privacy regulation applies to the
  // visitor's region. No cookie means no regulation (US today). Values:
  // a/m/p/s = analytics/marketing/preferences/sale-of-data, '1' yes, '0' no,
  // '' not yet answered.
  var consent = { cookie: null, api: null, api_state: 'pending' };
  (function () {
    var raw = cookie('_tracking_consent');
    if (!raw) return;
    var j = json(raw);
    if (!j) { consent.cookie = { raw: raw.slice(0, 80) }; return; }
    var c = (j.con && j.con.CMP) || {};
    consent.cookie = { reg: j.reg, region: j.region, a: c.a, m: c.m, p: c.p, s: c.s };
  })();
  function allowedFromCookie() {
    var c = consent.cookie;
    if (!c) return true;                       // no regulation applies
    if (c.raw) return null;
    if (!c.reg) return true;
    if (c.a === '1') return true;
    if (c.a === '0') return false;
    return null;                               // banner shown, not answered yet
  }
  var allowed = allowedFromCookie();

  /* ---------- identity ---------- */
  var now = Date.now();
  var vid = cookie('_mc_vid'), vidSource = vid ? 'cookie' : null;
  if (!vid) { vid = ls('mc_vid') || ''; if (vid) vidSource = 'storage'; }
  if (!vid) { vid = rid(); vidSource = 'new'; }
  var sid = cookie('_mc_sid'), sidSource = sid ? 'cookie' : null;
  if (!sid) {
    var m = json(ls('mc_sid'));
    if (m && m.id && now - m.ts < 1800000) { sid = m.id; sidSource = 'storage'; }
  }
  if (!sid) { sid = rid(); sidSource = 'new'; }

  var y8 = cookie('_shopify_y').slice(0, 8) || null;
  var s8 = cookie('_shopify_s').slice(0, 8) || null;
  var prev = json(ls('mc_prev'));
  var prevPath = prev && prev.path ? String(prev.path).slice(0, 300) : null;
  var prevAge = prev && prev.ts ? now - prev.ts : null;
  var yChanged = prev && prev.y8 && y8 ? prev.y8 !== y8 : null;
  var sChanged = prev && prev.s8 && s8 ? prev.s8 !== s8 : null;

  function persistIds() {
    setCookie('_mc_vid', vid, 365); setLs('mc_vid', vid);
    setCookie('_mc_sid', sid, 1 / 48); setLs('mc_sid', JSON.stringify({ id: sid, ts: now }));
    setLs('mc_prev', JSON.stringify({ path: path.slice(0, 300), ts: now, y8: y8, s8: s8 }));
  }

  /* ---------- first touch ---------- */
  var ft = json(cookie('_mc_ft')) || json(ls('mc_ft'));
  var pageTouch = null;
  if (landing) {
    pageTouch = {
      _mc_src: (utm.utm_source || refSource(refHost) || 'direct').slice(0, 60),
      _mc_med: (utm.utm_medium || (refHost ? 'referral' : 'none')).slice(0, 60),
      _mc_landing: path.slice(0, 255),
      _mc_ts: new Date(now).toISOString().slice(0, 19)
    };
    if (utm.utm_campaign) pageTouch._mc_camp = utm.utm_campaign.slice(0, 120);
    if (utm.utm_content) pageTouch._mc_content = utm.utm_content.slice(0, 120);
    if (utm.utm_term) pageTouch._mc_term = utm.utm_term.slice(0, 120); // Meta: the ad id
    if (refHost) pageTouch._mc_ref = refHost.slice(0, 120);
    if (utm.mc_from) pageTouch._mc_via = utm.mc_from.slice(0, 20); // handed off from this in-app browser
    var click = utm.ttclid ? 'tt:' + utm.ttclid : utm.fbclid ? 'fb:' + utm.fbclid : utm.gclid ? 'g:' + utm.gclid : '';
    if (click) pageTouch._mc_click = click.slice(0, 255);
  }
  // Keep the first touch we have, unless this landing carries a real signal
  // and the stored one was a plain direct visit.
  var ftIsNew = false;
  if (pageTouch && (!ft || (strongLanding && !ft._mc_click && ft._mc_src === 'direct'))) { ft = pageTouch; ftIsNew = true; }

  /* ---------- cart attributes ---------- */
  var cartCount = parseInt(ds.cartCount || '0', 10) || 0;
  var cartTagged = ds.cartTagged === '1';
  var cartWrite = null;
  var wroteThisPage = false;

  function attrsFor(touch) {
    var a = {};
    if (touch) for (var k in touch) if (touch[k]) a[k] = touch[k];
    a._mc_inapp = uaClass;
    a._mc_vid = vid;
    return a;
  }
  function writeCart(reason, touch, done) {
    if (wroteThisPage) return;
    wroteThisPage = true;
    cartWrite = reason;
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrsFor(touch) })
      }).then(function (r) { cartWrite = reason + (r.ok ? ':ok' : ':error'); cartTagged = cartTagged || r.ok; })
        .catch(function () { cartWrite = reason + ':error'; });
    } catch (e) { cartWrite = reason + ':error'; }
    if (done) done();
  }

  var ran = false;
  function whenAllowed() {
    if (ran) return;
    ran = true;
    persistIds();
    if (ftIsNew) { setCookie('_mc_ft', JSON.stringify(ft), 30); setLs('mc_ft', JSON.stringify(ft)); }
    if (isBot) return;
    // Landing with a fresh first touch: write now, while we still know where
    // she came from. This is the write that survives in-app browsers.
    if (ftIsNew && strongLanding) writeCart('landing', ft);
    // Cart exists but was never tagged (older cart, or the landing write
    // failed): tag it with whatever first touch we hold.
    else if (cartCount > 0 && !cartTagged && ft) writeCart('rewrite', ft);
  }

  // After the theme's add-to-cart resolves (theme.js dispatches this once
  // the drawer has re-rendered), make sure the cart carries our attributes.
  document.addEventListener('theme:cart:added', function () {
    if (allowed !== true || isBot) return;
    if (wroteThisPage || cartTagged) return;
    writeCart('added', ft || (strongLanding ? pageTouch : null));
  });

  /* ---------- in-app handoff ----------
     TikTok and Instagram open tapped links in Safari, which starts with no
     referrer and none of our cookies. Carry the source on the URL so the
     Safari page (and Shopify, and GA) still know where she came from. */
  var FORWARD = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ttclid', 'fbclid', 'gclid'];
  var carry = null;
  if (uaClass !== 'none' && !isBot) {
    carry = new URLSearchParams();
    FORWARD.forEach(function (k) { if (utm[k]) carry.set(k, utm[k]); });
    if (!carry.get('utm_source') && ft && ft._mc_src && ft._mc_src !== 'direct') {
      carry.set('utm_source', ft._mc_src);
      if (ft._mc_med && ft._mc_med !== 'none') carry.set('utm_medium', ft._mc_med);
      if (ft._mc_camp) carry.set('utm_campaign', ft._mc_camp);
      if (ft._mc_content) carry.set('utm_content', ft._mc_content);
      if (ft._mc_term) carry.set('utm_term', ft._mc_term);
      if (ft._mc_click) {
        var parts = ft._mc_click.split(':'); var key = { tt: 'ttclid', fb: 'fbclid', g: 'gclid' }[parts[0]];
        if (key && !carry.get(key)) carry.set(key, parts.slice(1).join(':'));
      }
    }
    carry.set('mc_from', uaClass);
  }
  function tag(a) {
    try {
      if (!carry || !a || a.dataset.mcTagged) return;
      var href = a.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#' || /^(mailto|tel|javascript|sms):/i.test(href)) return;
      var u = new URL(href, location.href);
      if (u.origin !== location.origin) return;
      if (/^\/(cart|checkout|checkouts|account|apps|search)(\/|$)/.test(u.pathname)) return;
      if (u.searchParams.get('utm_source') || u.searchParams.get('mc_from')) return;
      carry.forEach(function (v, k) { u.searchParams.set(k, v); });
      a.href = u.toString();
      a.dataset.mcTagged = '1';
    } catch (e) { /* leave the link alone */ }
  }
  if (carry) {
    try {
      // On load, for links the shopper long-presses or the app opens externally.
      Array.prototype.forEach.call(document.querySelectorAll('a[href]'), tag);
      // At tap time, for links the theme adds later (drawers, quick views).
      document.addEventListener('click', function (e) { var a = e.target && e.target.closest ? e.target.closest('a[href]') : null; if (a) tag(a); }, true);
    } catch (e) { /* ignore */ }
  }

  /* ---------- consent API (async) ---------- */
  function readApi(cp) {
    try {
      consent.api = {
        banner: cp.shouldShowBanner ? cp.shouldShowBanner() : null,
        analytics: cp.analyticsProcessingAllowed ? cp.analyticsProcessingAllowed() : null,
        marketing: cp.marketingAllowed ? cp.marketingAllowed() : null,
        sale: cp.saleOfDataAllowed ? cp.saleOfDataAllowed() : null,
        consent: cp.currentVisitorConsent ? cp.currentVisitorConsent() : null
      };
      consent.api_state = 'loaded';
      if (allowed === null) allowed = consent.api.analytics !== false;
    } catch (e) { consent.api_state = 'error'; }
  }
  var apiTimer = null;
  function resolveConsent() {
    if (apiTimer) { clearTimeout(apiTimer); apiTimer = null; }
    if (allowed === null) allowed = consent.cookie && consent.cookie.reg ? false : true;
    if (allowed) whenAllowed();
  }
  // No regulation, or consent already given: act now, before the shopper can
  // tap away. The API is still loaded below, for the diagnostics.
  if (allowed === true) whenAllowed();
  try {
    if (window.Shopify && Shopify.customerPrivacy) {
      readApi(Shopify.customerPrivacy); resolveConsent();
    } else if (window.Shopify && typeof Shopify.loadFeatures === 'function') {
      apiTimer = setTimeout(function () { consent.api_state = 'timeout'; resolveConsent(); }, 1500);
      Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
        if (!err && Shopify.customerPrivacy) readApi(Shopify.customerPrivacy);
        else consent.api_state = 'unavailable';
        resolveConsent();
      });
    } else { consent.api_state = 'unavailable'; resolveConsent(); }
  } catch (e) { consent.api_state = 'error'; resolveConsent(); }

  /* ---------- beacons ---------- */
  var pv = rid();
  var base = {
    pv: pv,
    vid_source: vidSource, sid_source: sidSource,
    cookie_ok: cookieOk, storage_ok: storageOk,
    y8: y8, s8: s8, y_changed: yChanged, s_changed: sChanged,
    consent: consent, allowed: allowed,
    path: path, referrer: referrer, ref_internal: refInternal, landing: landing,
    prev_path: prevPath, prev_age_ms: prevAge,
    utm: utm, ft: ft,
    cart_count: cartCount, cart_tagged: cartTagged,
    locksmith: !!window.Locksmith
  };
  function ids() { return allowed === true ? { vid: vid, sid: sid } : { vid: null, sid: null }; }
  function analyticsState() {
    var sa = window.ShopifyAnalytics;
    return {
      trekkie: !!(sa && sa.lib && sa.lib.trekkie),
      pixels: !!window.webPixelsManager,
      locksmith_ping: (function () {
        try { return performance.getEntriesByType('resource').some(function (e) { return /\/apps\/locksmith\/ping/.test(e.name); }); } catch (e) { return null; }
      })()
    };
  }

  // View: sent at once so a shopper who taps away in two seconds still counts.
  send(Object.assign({ kind: 'view', cart_write: ftIsNew && strongLanding ? 'landing' : null }, base, ids(), analyticsState()));

  // Leave: what had loaded by the time she left, and how the cart write ended.
  var left = false;
  function leave() {
    if (left) return; left = true;
    var dwell = (window.performance && performance.now) ? Math.round(performance.now() - t0) : Date.now() - startedAt;
    send(Object.assign({ kind: 'leave', pv: pv, path: path, ua_class: uaClass, consent: consent, allowed: allowed,
      cart_write: cartWrite, cart_tagged: cartTagged, dwell_ms: dwell, locksmith: !!window.Locksmith }, ids(), analyticsState()));
  }
  window.addEventListener('pagehide', leave);
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') leave(); });
})();
